/**
 * What a person a HOSTED app is shared with may change in its shared data — the share dialog's choice and the
 * label each person row shows. Same values and keys on iOS and Android (`apps.share.access.*`).
 *
 * Import-free so jest pins it. Everyone with access reads every item; the choice is only about changing them.
 */

export type DataAccess = 'EDIT_ALL' | 'EDIT_OWN';

/** The family default: everyone edits everything. The narrower choice is opt-in. */
export const DEFAULT_DATA_ACCESS: DataAccess = 'EDIT_ALL';

export const DATA_ACCESS_CHOICES: readonly { value: DataAccess; key: string }[] = [
  { value: 'EDIT_ALL', key: 'apps.share.access.editAll' },
  { value: 'EDIT_OWN', key: 'apps.share.access.editOwn' },
];

/** The row label for a share. Absent (an older server, or a share made before hosting) reads as EDIT_ALL. */
export function dataAccessLabelKey(access: DataAccess | undefined): string {
  return access === 'EDIT_OWN' ? 'apps.share.access.editOwn' : 'apps.share.access.editAll';
}

/**
 * The share POST body. A hosted app carries `dataAccess` and never lends tools (its data lives in Symphony, so
 * there is nothing of the owner's to lend — the dialog hides the section); an ordinary app is exactly the body
 * it was before, so nothing about non-hosted sharing moves.
 */
export function shareRequestBody(input: {
  appId: string;
  email: string;
  hosted: boolean;
  lendTools: { connectionId: string; toolName: string }[];
  dataAccess: DataAccess;
  confirmUnknown: boolean;
}): Record<string, unknown> {
  return {
    appId: input.appId,
    email: input.email,
    ...(!input.hosted && input.lendTools.length ? { lendTools: input.lendTools } : {}),
    ...(input.hosted ? { dataAccess: input.dataAccess } : {}),
    ...(input.confirmUnknown ? { confirmUnknown: true } : {}),
  };
}

/** A route's `dataAccess` value as one of the two, or undefined (refused by the route, never defaulted). */
export function parseDataAccess(value: unknown): DataAccess | undefined {
  return value === 'EDIT_ALL' || value === 'EDIT_OWN' ? value : undefined;
}

/** The bff's enum as the short form; zero/unknown is EDIT_ALL (the proto's documented reading). */
export function fromWireDataAccess(value: unknown): DataAccess | undefined {
  if (value === 'APP_DATA_EDIT_OWN' || value === 'EDIT_OWN') return 'EDIT_OWN';
  if (value === 'APP_DATA_EDIT_ALL' || value === 'EDIT_ALL' || value === 'UNKNOWN_APP_DATA_ACCESS') return 'EDIT_ALL';
  return undefined;
}
